import _ from 'lodash';
import React, { useState, useRef, useMemo, useCallback, useContext, useEffect } from 'react';
import Fuse from 'fuse.js';
// TODO: Import @material-ui from design-system folder
// eslint-disable-next-line restrict-imports/restrict-imports
import { MenuList, MenuItem, Popover } from '@material-ui/core';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToggle from 'react-use-v13/lib/useToggle';
import { TextInput } from 'design-system';
import { Button } from 'design-system/Button';
import { makeStyles } from 'design-system/styles';
import {
  LabelChoice,
  NestedLabelChoice,
  isNestedLabelChoice,
  getLabelChoiceDisplay,
  getLabelChoiceValue,
  getLabelChoiceHint,
} from '@scale/shared/types/params/annotation';
import {
  getChoice,
  generateChildToParentChoiceMapping,
} from '@scale/shared/annotation/labelChoiceHelpers';
import { getAllChoices } from '@scale/shared/annotation';
import Tooltip from 'design-system/Tooltip';
import HintTooltipIcon from '../../tasks/textcollection/HintTooltipIcon';
import useShortcut, {
  useShortcuts,
  ShortcutsContext,
  ShortcutMode,
} from 'react-scale/hooks/useShortcut';
import { Trigger } from 'react-scale/components/scalers/tasks/common/Shortcuts/Trigger';
import { AnnotationToolType } from '@scale/shared/types/events/AnnotationToolUsageEvent';
import { logEvent, Event } from 'react-scale/lib/events';

interface AttributeSelectSequenceProps {
  choices: LabelChoice[];
  selectedChoice?: string;
  handleSelect: (choice: LabelChoice) => void;
  recentChoices?: LabelChoice[];
  priorityLabels?: string[];
  isReadOnly?: boolean;
  autoSelect?: boolean;
  logMetadata?: Record<string, any>;
  shouldUseShortcuts?: boolean;
  labelShortcutMapping?: Record<string, string>;
}

export const AttributeSelectSequence: React.FC<AttributeSelectSequenceProps> = ({
  choices,
  selectedChoice,
  handleSelect,
  recentChoices,
  priorityLabels,
  isReadOnly,
  autoSelect,
  logMetadata,
  shouldUseShortcuts,
  labelShortcutMapping,
}) => {
  const classes = useChoiceMenuStyles();

  const { setShortcutMode } = useContext(ShortcutsContext);

  useEffect(() => {
    if (shouldUseShortcuts) {
      setShortcutMode(ShortcutMode.Selector);
      return () => {
        setShortcutMode(ShortcutMode.Labeler); // reenable on unmount
      };
    }
  }, []);

  const [isSelecting, toggleIsSelecting] = useToggle(false);
  const anchorEl = useRef<HTMLElement>();
  const startSelecting = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleIsSelecting();
    anchorEl.current = event.target;
  };

  // Store superchoice even if it can't be the selectedChoice
  const [currentChoice, setCurrentChoice] = useState(getChoice(choices, selectedChoice));

  // Keep track of the index of highlighted choice for UP and DOWN arrow key movement
  const [currentChoiceIndex, setCurrentChoiceIndex] = useState<number | undefined>();
  const orderedChoices = choices;
  const onSelect = useCallback(
    (choice: LabelChoice | undefined) => {
      setSearch('');
      setCurrentChoice(choice);
      // Only complete selection process if a leaf label is chosen
      if (!_.isNil(choice) && choice !== '' && !isNestedLabelChoice(choice)) {
        handleSelect(choice);
        toggleIsSelecting();
      }
    },
    [handleSelect, toggleIsSelecting],
  );

  const onCurrentChoiceUpOrDownKey = useCallback(
    (downKey: boolean) => {
      if (currentChoiceIndex === undefined) {
        setCurrentChoiceIndex(0);
        setCurrentChoice(orderedChoices[0]);
        return;
      }

      let nextIndex = currentChoiceIndex;
      if (downKey) {
        nextIndex = (nextIndex + 1) % orderedChoices.length;
      } else {
        nextIndex = (nextIndex - 1) % orderedChoices.length;
      }
      setCurrentChoiceIndex(nextIndex);
      setCurrentChoice(orderedChoices[nextIndex]);
    },
    [currentChoiceIndex, orderedChoices],
  );

  const onSelectRecentLog = (choice: LabelChoice | undefined, index?: number) => {
    logEvent(Event.AnnotationToolUsageEvent, {
      toolType: AnnotationToolType.LabelRecentChoices,
      nextValue: index ?? 0,
      ...(logMetadata as any),
    });
    onSelect(choice);
  };

  const onSelectLog = (choice: LabelChoice | undefined) => {
    logEvent(Event.AnnotationToolUsageEvent, {
      toolType: AnnotationToolType.LabelChoices,
      nextValue: choice ? getLabelChoiceDisplay(choice) : '',
      ...(logMetadata as any),
    });
    onSelect(choice);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSelectRecentLog(recentChoices?.[0]);
    }

    const shouldBlur = event.altKey || event.key === 'ArrowDown' || event.key === 'ArrowUp';

    if (shouldBlur && event.target instanceof HTMLElement) {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        onCurrentChoiceUpOrDownKey(event.key === 'ArrowDown');
      }
      event.target.blur();
    }
  };

  useShortcut({
    trigger: 'enter',
    description: 'Select first label',
    action: () => {
      if (currentChoiceIndex === undefined) {
        onSelectRecentLog(recentChoices?.[0]);
      } else {
        onSelect(currentChoice);
      }
    },
    disabled: !recentChoices?.length,
    mode: ShortcutMode.Selector,
  });

  useShortcut({
    trigger: 'down',
    description: 'Move cursor down',
    action: e => {
      e.stopImmediatePropagation();
      e.preventDefault();
      onCurrentChoiceUpOrDownKey(true);
    },
    disabled: !orderedChoices?.length,
    mode: ShortcutMode.Selector,
  });

  useShortcut({
    trigger: 'up',
    description: 'Move cursor up',
    action: e => {
      e.stopImmediatePropagation();
      e.preventDefault();
      onCurrentChoiceUpOrDownKey(false);
    },
    disabled: !orderedChoices?.length,
    mode: ShortcutMode.Selector,
  });

  // Text search for choices
  const [search, setSearch] = useState('');
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const searchIndex = useMemo(() => {
    const allChoices = getAllChoices(choices, true).map(choice => {
      let choiceWithDisplay: LabelChoice = _.cloneDeep(choice);
      if (!_.isString(choiceWithDisplay)) {
        choiceWithDisplay.display = choiceDisplay(choiceWithDisplay);
      } else {
        choiceWithDisplay = {
          display: choiceWithDisplay,
          choice: choiceWithDisplay,
          subchoices: [],
        };
      }
      return choiceWithDisplay;
    });
    return new Fuse(allChoices, {
      findAllMatches: true,
      includeMatches: true,
      threshold: 0.2,
      keys: ['display'],
    });
  }, [choices]);
  const searchResultChoices = useMemo(() => {
    if (search === '') {
      return [];
    }
    return searchIndex.search(search).map(result => result.item);
  }, [searchIndex, search]);

  const childToParentMap = useMemo(() => {
    return generateChildToParentChoiceMapping(choices);
  }, [choices]);

  const currentChoiceObject = getChoice(choices, currentChoice, true);

  const parentChoiceObject = !_.isNil(currentChoiceObject)
    ? childToParentMap.get(currentChoiceObject)
    : undefined;
  // Find level of choice nesting to display
  let currentChoiceLevel = choices;
  if (!_.isNil(currentChoiceObject)) {
    // Leaf choice, display all sibling choices
    if (_.isString(currentChoiceObject) || !currentChoiceObject.subchoices) {
      currentChoiceLevel = (parentChoiceObject as NestedLabelChoice)?.subchoices ?? choices;
    } else {
      // Superchoice, display all subchoices
      currentChoiceLevel = currentChoiceObject.subchoices;
    }
  }

  const choicesForShortcut = useMemo(
    () => (search ? searchResultChoices : currentChoiceLevel),
    [currentChoiceLevel, search, searchResultChoices],
  );

  useShortcuts(
    Array(10)
      .fill(0)
      .map((_, idx) => ({
        trigger: `alt+${(idx + 1) % 10}`,
        description: 'Selects the corresponding label',
        action: e => {
          e.stopPropagation();
          e.preventDefault();
          onSelect(choicesForShortcut[idx]);
        },
        disabled: idx > choicesForShortcut.length,
        mode: ShortcutMode.Selector,
      })),
  );

  const priorityLabelSet = useMemo(() => new Set(priorityLabels), [priorityLabels]);
  const priorityChoices = useMemo(
    () =>
      [...childToParentMap.keys()].filter(
        ch => priorityLabelSet.has(choiceDisplay(ch)) && (_.isString(ch) || !ch.subchoices),
      ),
    [childToParentMap, priorityLabelSet],
  );

  const attributeSelector = (
    <div className={classes.menuWrapper}>
      <div style={{ padding: 16, paddingBottom: 4, height: 56 }}>
        <TextInput
          placeholder="Filter labels..."
          value={search}
          onChange={onSearch}
          fullWidth
          autoFocus
          onKeyDown={handleKeyDown}
        />
      </div>
      {recentChoices && recentChoices.length > 0 && !search && (
        <FlatChoiceMenuList
          title="Recent choices"
          showShortcut
          choices={recentChoices}
          onSelect={onSelectRecentLog}
        />
      )}
      {priorityChoices.length > 0 && !search && (
        <FlatChoiceMenuList
          title="Priority choices"
          choices={priorityChoices}
          onSelect={onSelectLog}
        />
      )}
      <ChoiceMenuList
        choices={choicesForShortcut}
        allChoices={choices}
        onSelect={onSelectLog}
        currentChoice={currentChoice}
        includeParents={!search}
        childToParentMap={childToParentMap}
        priorityLabels={priorityLabelSet}
        labelShortcutMapping={labelShortcutMapping}
      />
    </div>
  );

  if (autoSelect) {
    return <div>{attributeSelector}</div>;
  } else {
    const handleClose = () => {
      if (isSelecting) toggleIsSelecting();
    };
    return (
      <>
        <Button
          disabled={isReadOnly}
          onClick={startSelecting}
          endIcon={!isReadOnly && <FontAwesomeIcon icon="chevron-down" />}
        >
          {choiceDisplay(getChoice(choices, selectedChoice) ?? '')}
        </Button>
        <Popover
          elevation={9}
          open={isSelecting}
          anchorEl={anchorEl.current}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          BackdropProps={{ invisible: true }}
          onClose={handleClose}
        >
          <div>{attributeSelector}</div>
        </Popover>
      </>
    );
  }
};

interface FlatChoiceMenuListProps {
  title: string;
  choices: LabelChoice[];
  onSelect: (label: LabelChoice, index?: number) => void;
  showShortcut?: boolean;
}

const FlatChoiceMenuList: React.FC<FlatChoiceMenuListProps> = props => {
  const { title, choices, showShortcut, onSelect } = props;

  const classes = useChoiceMenuStyles();

  return (
    <MenuList classes={{ root: classes.menu }}>
      <MenuItem className={classNames(classes.parentName)}>{title}</MenuItem>
      {choices.map((choice, index) => {
        const display = choiceDisplay(choice);
        const hint = getLabelChoiceHint(choice);
        const value = getLabelChoiceValue(choice);

        return (
          <MenuItem
            key={display}
            className={classNames(classes.itemName)}
            onClick={() => onSelect(choice, index)}
          >
            <div style={{ marginRight: '18px' }}></div>
            {display}
            {index === 0 && showShortcut ? (
              <div style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
                <Trigger trigger="Enter" />
              </div>
            ) : null}
            {hint && <HintTooltipIcon key={value} hint={hint} />}
          </MenuItem>
        );
      })}
    </MenuList>
  );
};

interface ChoiceMenuListProps {
  choices: LabelChoice[];
  allChoices: LabelChoice[];
  onSelect: (choice: LabelChoice | undefined) => void;
  currentChoice?: LabelChoice | string;
  includeParents?: boolean;
  childToParentMap: Map<LabelChoice, LabelChoice | undefined>;
  priorityLabels: Set<string>;
  labelShortcutMapping?: Record<string, string>;
}

const ChoiceMenuList: React.FC<ChoiceMenuListProps> = props => {
  const {
    choices,
    onSelect,
    currentChoice,
    allChoices,
    includeParents,
    childToParentMap,
    priorityLabels,
    labelShortcutMapping,
  } = props;

  const parentChoices: {
    level?: LabelChoice;
    parentLevel?: LabelChoice;
  }[] = useMemo(() => {
    const currentChoiceObject = getChoice(allChoices, currentChoice, true);
    const parents: { level?: LabelChoice }[] = [];

    let currentLevel = currentChoiceObject;
    if (!_.isNil(currentLevel) && !isNestedLabelChoice(currentLevel)) {
      currentLevel = childToParentMap.get(currentLevel);
    }
    while (!_.isNil(currentLevel)) {
      const parentLevel = childToParentMap.get(currentLevel);
      parents.unshift({ level: currentLevel });
      currentLevel = parentLevel;
    }
    parents.unshift({ level: undefined });
    return parents;
  }, [allChoices, currentChoice]);

  const classes = useChoiceMenuStyles();

  const createOnClick =
    (choice: LabelChoice | undefined) => (e?: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      e?.stopPropagation();
      onSelect(choice);
    };

  return (
    <MenuList classes={{ root: classes.menu }}>
      {includeParents &&
        parentChoices.map(({ level }, i) => {
          const display = choiceDisplay(level);
          const hint = getLabelChoiceHint(level);
          const value = getLabelChoiceValue(level);

          return (
            <MenuItem
              key={`${display}-parent`}
              className={classNames(classes.parentName)}
              onClick={e => createOnClick(level)(e)}
              selected={false}
              autoFocus={false}
            >
              <div>
                {i !== parentChoices.length - 1 && (
                  <FontAwesomeIcon
                    icon="chevron-left"
                    style={{ marginRight: '10px', fontSize: '14px' }}
                  />
                )}{' '}
              </div>
              {_.isNil(level) ? 'All choices' : display}
              {!_.isNil(level) && hint && <HintTooltipIcon key={value} hint={hint} />}
            </MenuItem>
          );
        })}
      {choices &&
        choices.map((choice, idx) => {
          const selected =
            !_.isNil(props.currentChoice) && props.currentChoice === getLabelChoiceValue(choice);
          const display = choiceDisplay(choice);
          const hint = getLabelChoiceHint(choice);
          const value = getLabelChoiceValue(choice);
          const priorityLabel = priorityLabels.has(display);
          const triggerText = labelShortcutMapping?.[value]
            ? `Alt + ${labelShortcutMapping[value]}`
            : idx < 10
            ? `Alt + ${(idx + 1) % 10}`
            : '';

          if (!_.isString(choice) && choice.subchoices) {
            return (
              <Tooltip
                title={priorityLabel ? 'Priority label' : ''}
                placement="top"
                arrow
                key={idx}
              >
                <MenuItem
                  key={`${display}-nested`}
                  className={classNames(classes.parentName)}
                  onClick={e => createOnClick(choice)(e)}
                >
                  <div style={{ marginRight: '18px' }}></div>
                  {display}
                  {triggerText ? (
                    <div style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
                      <Trigger trigger={triggerText} />
                    </div>
                  ) : null}
                  {hint && <HintTooltipIcon key={value} hint={hint} />}
                  <div>
                    <FontAwesomeIcon
                      icon="chevron-right"
                      style={{ marginLeft: '10px', fontSize: '14px' }}
                    />
                  </div>
                  {priorityLabel && (
                    <FontAwesomeIcon
                      icon="arrow-up"
                      style={{ marginLeft: 'auto', fontSize: '14px' }}
                      color="dodgerblue"
                    />
                  )}
                </MenuItem>
              </Tooltip>
            );
          }

          const menuItem = (
            <Tooltip title={priorityLabel ? 'Priority choice' : ''} placement="top" arrow key={idx}>
              <MenuItem
                key={display}
                className={classNames({
                  [classes.selected]: selected,
                  [classes.itemName]: !selected,
                })}
                selected={selected}
                onClick={e => createOnClick(choice)(e)}
                autoFocus={selected}
              >
                <div style={{ marginRight: '18px' }}></div>
                {display}
                {triggerText ? (
                  <div style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
                    <Trigger trigger={triggerText} />
                  </div>
                ) : null}

                {priorityLabel && (
                  <FontAwesomeIcon
                    icon="arrow-up"
                    style={{ marginLeft: 'auto', fontSize: '14px' }}
                    color="dodgerblue"
                  />
                )}

                {hint && <HintTooltipIcon key={value} hint={hint} />}
              </MenuItem>
            </Tooltip>
          );

          return menuItem;
        })}
    </MenuList>
  );
};

const choiceDisplay = (choice: LabelChoice | undefined) => {
  if (_.isNil(choice)) {
    return '';
  } else {
    return getLabelChoiceDisplay(choice);
  }
};

const useChoiceMenuStyles = makeStyles({
  menuWrapper: {
    minWidth: 240,
    maxHeight: 320,
    overflowY: 'scroll',
  },
  menu: {
    pointerEvents: 'all',
    minWidth: 240,
    border: 1,
  },
  itemName: {
    fontSize: 14,
  },
  parentName: {
    fontSize: 14,
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  selected: {
    fontSize: 14,
    backgroundColor: 'lightgray',
  },
});
